<template>
  <!--
    Divergência pinta o container inteiro com o padrão de alerta vermelho do painel
    (`border-negative bg-red-50`, o mesmo do LabelBadge vermelho) — em qualquer status,
    para a fila de exceções saltar aos olhos sem depender do filtro.
  -->
  <div
    class="border rounded-lg p-4 py-2 mb-4"
    :class="hasDivergentEntries ? 'border-negative bg-red-50' : 'border-gray-200'"
    data-testid="ds-attendance-card"
  >
    <!--
      F2/M3 — variante por espécie. O card de FOLHA abaixo NÃO muda: `kind` ausente é
      backend anterior à F2 e cai no `v-else`, com o mesmo HTML de hoje.
    -->
    <div
      v-if="isActivity"
      class="flex flex-col gap-2 py-2"
      data-testid="ds-attendance-card-activity"
    >
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-[#52526F] text-xs font-bold leading-[14px] tracking-[0.06px]"
          >#{{ attendanceId }}</span
        >
        <h4 class="text-textPrimary text-md font-bold">Registro de Atividade · Jornada</h4>
        <StatusBadge :status="status" />
      </div>

      <div class="text-sm text-textPrimary flex flex-col gap-0.5">
        <span>
          Tipo: <b>{{ activityTypeText }}</b>
          <template v-if="activityDescription"> — “{{ activityDescription }}”</template>
          <template v-if="doctorLabel">
            · <b>{{ doctorLabel }}</b>
          </template>
        </span>
        <span>
          Plantão <b>{{ formattedDate }}</b> · {{ unit || 'Unidade não informada' }} ·
          {{ schedule || 'Escala não informada' }}
        </span>
        <span>
          <b>{{ attachmentLabel }}</b>
          <template v-if="submitterLabel">
            · enviado por <b>{{ submitterLabel }}</b></template
          >
        </span>
      </div>

      <!-- Pernas do plantão: o que o sistema já tem × o que este registro declara (M3, ①). -->
      <div v-if="activityLegs.length" class="flex flex-wrap gap-2">
        <span
          v-for="leg in activityLegs"
          :key="leg.id"
          :data-testid="`ds-attendance-card-leg-${leg.id}`"
          class="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-textPrimary"
        >
          <b>{{ leg.label }}</b> {{ leg.value }}
          <span
            :class="[
              'rounded-full px-2 py-0.5 text-[10px] font-bold',
              leg.pending ? 'bg-orange-50 text-alert' : 'bg-green-50 text-sucess'
            ]"
            >{{ leg.state }}</span
          >
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <LabelBadge
          v-for="flag in riskFlagLabels"
          :key="flag"
          data-testid="ds-attendance-card-risk-chip"
          :label="flag"
          color="orange"
          size="small"
          title="Critério que elevou o risco deste registro"
        />
        <LabelBadge
          v-if="afterClosure"
          data-testid="ds-attendance-card-after-closure"
          label="Recebido após fechamento"
          color="orange"
          size="small"
          title="O período do plantão já estava fechado quando este registro chegou"
        />
        <LabelBadge
          v-if="legacyMigrated"
          data-testid="ds-attendance-card-legacy-migrated"
          label="Migrado da Avaliação de Evidências"
          color="gray"
          size="small"
          title="Linha nascida da migração retroativa da fila antiga"
        />
        <LabelBadge
          v-if="supersededBySheet"
          data-testid="ds-attendance-card-superseded"
          label="Superado pela folha"
          color="gray"
          size="small"
          title="O horário efetivo do plantão veio da folha auditada, não deste registro"
        />
        <LabelBadge
          v-if="hasAntecipe"
          data-testid="ds-attendance-card-antecipe"
          label="Antecipe"
          color="orange"
          size="small"
          title="Algum plantão deste registro tem antecipação (Antecipe) solicitada ou paga"
        />
        <LabelBadge
          v-if="hasInCash"
          data-testid="ds-attendance-card-a-vista"
          label="À vista"
          color="blue"
          size="small"
          title="Algum plantão deste registro é pago à vista"
        />
        <LabelBadge
          v-if="stationChangedAfterAudit"
          data-testid="ds-attendance-card-station-changed"
          label="Plantão alterado após auditoria"
          color="orange"
          size="small"
          title="A vaga/plantão foi criada, alterada ou excluída depois do início da auditoria"
        />
        <LabelBadge
          v-if="manualReviewReasonText"
          data-testid="ds-attendance-card-manual-review-reason"
          :label="manualReviewReasonText"
          color="orange"
          size="small"
          title="Motivo pelo qual este registro foi para a fila de revisão manual"
        />
        <LabelBadge
          v-if="isAutomaticallyApproved"
          data-testid="ds-attendance-card-approval-mode"
          :label="APPROVAL_MODE_LABELS.AUTOMATIC"
          color="green"
          size="small"
          title="Aprovado automaticamente: todos os critérios da política bateram"
        />
        <div class="ml-auto">
          <OutlinedButton
            data-testid="ds-attendance-card-ver-detalhes-button"
            text="Ver detalhes"
            width="130px"
            height="32px"
            @click="$emit('details', props.attendanceId)"
          />
        </div>
      </div>
    </div>

    <div v-else class="flex flex-wrap gap-y-3 gap-x-4 sm:gap-x-0 items-center">
      <div
        class="basis-[calc(50%-8px)] sm:basis-[140px] shrink-0 min-w-0 flex flex-col text-sm text-start"
        :title="`#${attendanceId} - ${formattedDate}`"
      >
        <span class="text-[#52526F] text-xs font-bold truncate leading-[14px] tracking-[0.06px]"
          >#{{ attendanceId }}</span
        >
        <span class="text-textPrimary text-md mb-1 font-bold truncate">Data do plantão</span>
        <span class="truncate">{{ formattedDate }}</span>
        <!--
          Pacote 2026-09-10 — indicativo de pagamento: algum plantão do registro em Antecipe
          (antecipação solicitada ou paga) / À vista. Vem calculado do backend (#2750).
        -->
        <div v-if="hasAntecipe || hasInCash" class="flex flex-wrap gap-1 mt-1">
          <LabelBadge
            v-if="hasAntecipe"
            data-testid="ds-attendance-card-antecipe"
            label="Antecipe"
            color="orange"
            size="small"
            title="Algum plantão deste registro tem antecipação (Antecipe) solicitada ou paga"
          />
          <LabelBadge
            v-if="hasInCash"
            data-testid="ds-attendance-card-a-vista"
            label="À vista"
            color="blue"
            size="small"
            title="Algum plantão deste registro é pago à vista"
          />
        </div>
      </div>

      <div
        class="basis-[calc(50%-8px)] sm:basis-[130px] shrink-0 min-w-0 flex flex-col text-sm text-start sm:border-l sm:border-gray-200 sm:pl-4"
        :title="formattedSubmissionDate"
      >
        <span class="text-textPrimary text-md mb-1 font-bold truncate">Data upload</span>
        <span class="truncate">{{ formattedSubmissionDate }}</span>
      </div>

      <div
        class="basis-[calc(50%-8px)] sm:basis-[130px] grow shrink-0 min-w-0 flex flex-col text-sm text-start sm:pl-4"
        :title="`${schedule || 'Não informada'} - ${unit || 'Não informada'}`"
      >
        <span class="text-textPrimary font-bold text-md mb-1 truncate">Escala</span>
        <span class="truncate">{{ schedule || 'Não informada' }}</span>
        <small class="text-textPrimary truncate block">{{ unit || 'Não informada' }}</small>
      </div>

      <div
        class="basis-[calc(50%-8px)] sm:basis-[110px] grow shrink-0 min-w-0 flex flex-col text-sm text-start sm:pl-4"
        :title="shifts"
      >
        <span class="text-textPrimary text-md mb-1 font-bold flex items-center gap-2">
          <div class="relative inline-flex items-center">
            <i class="pi pi-briefcase text-primary"></i>
            <Badge
              v-if="shiftsCount > 1"
              :value="shiftsCount"
              severity="info"
              class="absolute -top-2 -right-2 scale-75"
            />
          </div>
          Turno(s)
        </span>
        <span class="truncate">{{ shifts }}</span>
      </div>

      <div
        class="basis-[calc(50%-8px)] sm:basis-[130px] grow shrink-0 min-w-0 flex flex-col text-sm text-start sm:pl-4"
        :title="DS_ATTENDANCE_STATUS_LABELS[status]"
      >
        <span class="text-textPrimary font-bold text-md mb-1 truncate">Status</span>
        <div class="flex flex-wrap items-center gap-2">
          <StatusBadge :status="status" />
          <!--
            Vaga/plantão mudou depois que a auditoria começou: âmbar ("requer atenção",
            não bloqueia), para o time de auditoria revisar os pontos já conciliados.
          -->
          <LabelBadge
            v-if="stationChangedAfterAudit"
            data-testid="ds-attendance-card-station-changed"
            label="Plantão alterado após auditoria"
            color="orange"
            size="small"
            title="A vaga/plantão foi criada, alterada ou excluída depois do início da auditoria"
          />
          <!--
            Fase 1 do fluxo de auditoria (spec 2026-09-11) — a escala não tem plantão com
            médico na data/turnos do registro. Cinza de propósito: é constatação, não alerta;
            o registro já saiu da fila padrão e da cobrança de prazo pelo backend.
          -->
          <LabelBadge
            v-if="hasScheduleExpected === false"
            data-testid="ds-attendance-card-no-schedule-expected"
            :label="NO_SCHEDULE_EXPECTED_LABEL"
            color="gray"
            size="small"
            :title="noScheduleExpectedTooltip"
          />
          <!--
            Fase 2 do fluxo de auditoria (spec 2026-09-11, §6.3) — por que a máquina NÃO
            aprovou sozinha. Âmbar: exige ação do auditor, mas não é erro do registro.
          -->
          <LabelBadge
            v-if="manualReviewReasonText"
            data-testid="ds-attendance-card-manual-review-reason"
            :label="manualReviewReasonText"
            color="orange"
            size="small"
            title="Motivo pelo qual este registro foi para a fila de revisão manual"
          />
          <!--
            Verde: aprovado sem toque humano. O auditor precisa distinguir de relance o que
            ele aprovou do que o motor aprovou — é a leitura que o Eixo 3 pede.
          -->
          <LabelBadge
            v-if="isAutomaticallyApproved"
            data-testid="ds-attendance-card-approval-mode"
            :label="APPROVAL_MODE_LABELS.AUTOMATIC"
            color="green"
            size="small"
            title="Aprovado automaticamente: todos os critérios da política bateram"
          />
        </div>
      </div>

      <div class="basis-full sm:basis-auto sm:ml-auto shrink-0 flex justify-end">
        <OutlinedButton
          data-testid="ds-attendance-card-ver-detalhes-button"
          text="Ver detalhes"
          width="130px"
          height="32px"
          @click="$emit('details', props.attendanceId)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  ACTIVITY_RISK_FLAG_LABELS,
  ACTIVITY_TYPE_LABELS,
  ActivityType,
  ContainerKind,
  CoveredLegs,
  DSAttendanceStatus,
  DS_ATTENDANCE_STATUS_LABELS,
  EFFECTIVE_SOURCE_KIND_LABELS,
  EffectiveSourceKind,
  type ActivityRiskFlag
} from '@/constants/ds-attendance.constants'
import type { DSAttendanceEffectiveSummary } from '@/types/ds-attendance.types'
import { formatDateInOffset } from '@/utils/date'
import { toHHmm } from '../utils/activity-time'
import {
  NO_SCHEDULE_EXPECTED_LABEL,
  buildNoScheduleExpectedTooltip
} from '../utils/prefill-empty-state'
import {
  APPROVAL_MODE_LABELS,
  DSAttendanceApprovalMode,
  manualReviewReasonLabel
} from '@/constants/ds-attendance-auto-approval.constants'

const props = defineProps({
  attendanceId: {
    type: [Number, String],
    required: true
  },
  date: {
    type: String,
    default: ''
  },
  submissionDate: {
    type: String,
    default: ''
  },
  unit: {
    type: String,
    default: ''
  },
  schedule: {
    type: String,
    default: ''
  },
  shifts: {
    type: String,
    default: ''
  },
  shiftsCount: { type: Number, default: 0 },
  /** Pacote 2026-09-10 — pílulas "Antecipe" / "À vista" (backend #2750). */
  hasAntecipe: { type: Boolean, default: false },
  hasInCash: { type: Boolean, default: false },
  hasDivergentEntries: { type: Boolean, default: false },
  stationChangedAfterAudit: { type: Boolean, default: false },
  /**
   * Fase 1 do fluxo de auditoria — tri-estado de propósito: `undefined` é backend anterior
   * à fase 1 (nada a dizer) e só `false` significa "a escala não prevê plantão aqui".
   * O `default: undefined` desliga o cast de Boolean ausente → `false` do Vue.
   */
  hasScheduleExpected: { type: Boolean, default: undefined },
  /**
   * Fase 2 do fluxo de auditoria (spec 2026-09-11, §6) — motivo da revisão manual e modo de
   * aprovação. `null`/ausente em backend anterior à fase 2: nenhum chip aparece.
   */
  manualReviewReason: { type: String as () => string | null, default: null },
  approvalMode: { type: String as () => string | null, default: null },
  /**
   * F2/M3 — espécie do container. Ausente/`FOLHA` mantém o card de hoje, byte a byte;
   * `ATIVIDADE` troca o corpo do card pela variante de Registro de Atividade.
   */
  kind: { type: String as () => string | null, default: null },
  activityType: { type: String as () => string | null, default: null },
  /** Rótulo vindo do backend; só usado quando o código não está no catálogo local. */
  activityTypeLabel: { type: String as () => string | null, default: null },
  activityDescription: { type: String as () => string | null, default: null },
  coveredLegs: { type: String as () => string | null, default: null },
  declaredCheckin: { type: String as () => string | null, default: null },
  declaredCheckout: { type: String as () => string | null, default: null },
  /** Fuso da escala do plantão (`-03:00`). Ausente, o card assume Brasília. */
  timezone: { type: String as () => string | null, default: null },
  doctorName: { type: String as () => string | null, default: null },
  doctorCrm: { type: String as () => string | null, default: null },
  attachmentCount: { type: Number as () => number | null, default: null },
  submittedByName: { type: String as () => string | null, default: null },
  /** Chaves `true` viram chips explicativos. O SCORE numérico nunca é exibido (§13). */
  activityRiskFlags: {
    type: Object as () => Partial<Record<ActivityRiskFlag, boolean>> | null,
    default: null
  },
  effectiveSummary: {
    type: Object as () => DSAttendanceEffectiveSummary | null,
    default: null
  },
  afterClosure: { type: Boolean, default: false },
  legacyMigrated: { type: Boolean, default: false },
  status: {
    type: String as () => DSAttendanceStatus,
    required: true,
    validator: (val: DSAttendanceStatus) => Object.values(DSAttendanceStatus).includes(val)
  }
})

defineEmits<{
  (e: 'details', attendanceId: number | string): void
}>()

const formattedDate = computed(() => {
  if (props.date) {
    try {
      return format(new Date(props.date + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })
    } catch (e) {
      return props.date
    }
  }
  return ''
})

/** Rótulo pt-BR do motivo; código desconhecido aparece cru em vez de virar chip vazio. */
const manualReviewReasonText = computed(() => manualReviewReasonLabel(props.manualReviewReason))

const isAutomaticallyApproved = computed(
  () => props.approvalMode === DSAttendanceApprovalMode.AUTOMATIC
)

/** Tooltip do chip cinza — usa a data já formatada em dd/MM/yyyy que o card exibe. */
const noScheduleExpectedTooltip = computed(() =>
  buildNoScheduleExpectedTooltip({
    scheduleName: props.schedule,
    date: formattedDate.value,
    shifts: props.shifts
  })
)

/* --------------------------------------------------------------------------- *
 * F2/M3 — variante "Registro de Atividade" do card.
 * --------------------------------------------------------------------------- */

const isActivity = computed(() => props.kind === ContainerKind.ATIVIDADE)

/** Catálogo local primeiro; o rótulo do backend só cobre código que ainda não conhecemos. */
const activityTypeText = computed(
  () =>
    ACTIVITY_TYPE_LABELS[props.activityType as ActivityType] ??
    props.activityTypeLabel ??
    props.activityType ??
    'Tipo não informado'
)

const doctorLabel = computed(() => {
  if (!props.doctorName) return ''
  return props.doctorCrm ? `${props.doctorName} (CRM ${props.doctorCrm})` : props.doctorName
})

const attachmentLabel = computed(() => {
  const count = props.attachmentCount
  if (count === null || count === undefined) return 'Anexos não informados'
  return count === 1 ? '1 anexo' : `${count} anexos`
})

const submitterLabel = computed(() => props.submittedByName ?? '')

/**
 * `HH:mm` no fuso da ESCALA — o mesmo `toHHmm` da tela de auditoria (I12/I1). Ler com
 * `new Date` convertia para o fuso do navegador e fazia o card e a auditoria mostrarem
 * horários diferentes para o mesmo registro. Valor não parseável volta cru em vez de sumir.
 */
function formatHour(value: string | null): string {
  if (!value) return ''
  return toHHmm(value, props.timezone ?? undefined) || value
}

function sourceLabel(source: string | null | undefined): string {
  if (!source) return ''
  return EFFECTIVE_SOURCE_KIND_LABELS[source as EffectiveSourceKind] ?? source
}

interface ActivityLeg {
  id: 'checkin' | 'checkout'
  label: string
  value: string
  state: string
  pending: boolean
}

function coversLeg(leg: 'checkin' | 'checkout'): boolean {
  if (props.coveredLegs === CoveredLegs.BOTH) return true
  return props.coveredLegs === (leg === 'checkin' ? CoveredLegs.CHECKIN : CoveredLegs.CHECKOUT)
}

/**
 * Uma linha por perna: a que ESTE registro declara aparece como "a auditar" (ou "aprovado",
 * depois do veredito); a que o sistema já resolveu por outra fonte aparece como "já tem",
 * com o nome da fonte. Perna sem nada a dizer não vira chip vazio.
 */
function buildLeg(id: 'checkin' | 'checkout', label: string): ActivityLeg | null {
  const declared = id === 'checkin' ? props.declaredCheckin : props.declaredCheckout
  const effective =
    id === 'checkin' ? props.effectiveSummary?.checkin : props.effectiveSummary?.checkout
  const approved = props.status === DSAttendanceStatus.APPROVED

  if (coversLeg(id) && declared) {
    return {
      id,
      label,
      value: `declarado ${formatHour(declared)}`,
      state: approved ? 'aprovado' : 'a auditar',
      pending: !approved
    }
  }

  if (effective?.value) {
    const source = sourceLabel(effective.source)
    return {
      id,
      label,
      value: source ? `${formatHour(effective.value)} · ${source}` : formatHour(effective.value),
      state: 'já tem',
      pending: false
    }
  }

  if (coversLeg(id)) {
    return {
      id,
      label,
      value: 'declarado —',
      state: approved ? 'aprovado' : 'a auditar',
      pending: !approved
    }
  }

  return null
}

const activityLegs = computed<ActivityLeg[]>(() =>
  [buildLeg('checkin', 'Check-in'), buildLeg('checkout', 'Check-out')].filter(
    (leg): leg is ActivityLeg => leg !== null
  )
)

/**
 * Só os critérios marcados como `true`, na ordem de declaração do catálogo. O card NÃO
 * mostra o `activityRiskScore`: número sem explicação não é acionável (§13 do spec).
 */
const riskFlagLabels = computed<string[]>(() => {
  const flags = props.activityRiskFlags
  if (!flags) return []
  return (Object.keys(ACTIVITY_RISK_FLAG_LABELS) as ActivityRiskFlag[])
    .filter((key) => flags[key] === true)
    .map((key) => ACTIVITY_RISK_FLAG_LABELS[key])
})

/** Aprovado, mas quem venceu o ranking da perna foi a folha auditada (mockup M3, card 4). */
const supersededBySheet = computed(() => {
  if (props.status !== DSAttendanceStatus.APPROVED) return false
  const summary = props.effectiveSummary
  if (!summary) return false
  return (['checkin', 'checkout'] as const).some(
    (leg) => coversLeg(leg) && summary[leg]?.source === EffectiveSourceKind.SHEET
  )
})

/**
 * Data do upload no fuso da ESCALA, pelo mesmo motivo do `formatHour` acima: com
 * `new Date(iso)` puro um upload de madrugada trocava de dia conforme o fuso da
 * máquina de quem olha, e dois auditores viam datas diferentes para o mesmo
 * registro. Era a única data do card lida sem offset explícito.
 */
const formattedSubmissionDate = computed(() => {
  if (!props.submissionDate) return ''
  return formatDateInOffset(props.submissionDate, props.timezone ?? undefined) ?? ''
})
</script>
